import { useCallback, useId } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Checkbox } from '@xing-com/checkbox';
import { IconInfo, IconWarning } from '@xing-com/icons';
import { Label } from '@xing-com/label';
import { List, ListItem } from '@xing-com/list';
import {
  scale080,
  scale110,
  spaceM,
  spaceL,
  spaceXS,
  xdlColorError,
} from '@xing-com/tokens';
import { Meta } from '@xing-com/typography';

export type CheckboxGroupFieldProps<T extends FieldValues> = {
  control: Control<T>;
  path: Path<T>;
  options: { value: string; label: string }[];
  required?: boolean;
  errorMessage?: string;
  helperMessage?: string;
  requiredErrorMessage?: string;
  minRequiredOptions?: number;
  maxRequiredOptions?: number;
  'aria-labelledby'?: string;
};

const multiselectValidator: (
  min: number,
  max: number,
  value: string[]
) => boolean = (min, max, value) => {
  return value.length
    ? value.length >= min && (!max || value.length <= max)
    : true;
};

export const CheckboxGroupField = <T extends FieldValues>({
  control,
  path,
  options,
  required = false,
  errorMessage,
  helperMessage,
  requiredErrorMessage,
  minRequiredOptions = 0,
  maxRequiredOptions = 0,
  'aria-labelledby': ariaLabelledBy,
}: CheckboxGroupFieldProps<T>): React.ReactElement => {
  const checkboxGroupId = useId();
  const validate = useCallback(
    (value: string[]) => {
      return multiselectValidator(
        minRequiredOptions,
        maxRequiredOptions,
        value
      );
    },
    [minRequiredOptions, maxRequiredOptions]
  );
  const {
    field: { onChange, value: checkedValues, ref, onBlur },
    fieldState: { error, isDirty },
  } = useController({
    control,
    name: path,
    rules: { required: required, validate },
  });

  const fieldErrorMessage = error
    ? error.type === 'required'
      ? requiredErrorMessage
      : errorMessage
    : undefined;

  return (
    <StyledFieldset aria-labelledby={ariaLabelledBy}>
      {fieldErrorMessage ? (
        <ErrorMessage
          lineHeight="dense"
          role="alert"
          size="xsmall"
          data-testid="checkbox-group-error-msg"
        >
          <ErrorIcon aria-hidden="true" />
          <FormattedMessage
            id={fieldErrorMessage}
            values={{ min: minRequiredOptions, max: maxRequiredOptions }}
          />
        </ErrorMessage>
      ) : helperMessage ? (
        <HelperMessage
          lineHeight="dense"
          size="xsmall"
          data-testid="checkbox-group-helper-msg"
        >
          <InfoIcon aria-hidden="true" />
          <FormattedMessage id={helperMessage} />
        </HelperMessage>
      ) : null}
      <StyledList>
        {options.map(({ value, label }, index) => (
          <StyledListItem key={value}>
            <StyledLabel>
              <Checkbox
                name={`checkbox-${checkboxGroupId}`}
                error={!!error}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const updatedList = event.target.checked
                    ? [...checkedValues, value]
                    : checkedValues.filter(
                        (currentValue: string) => currentValue !== value
                      );
                  onChange(updatedList);
                }}
                on={checkedValues.includes(value)}
                {...(index === 0 ? { ref } : {})}
                {...(index === options.length - 1 || isDirty ? { onBlur } : {})}
                aria-labelledby={`${checkboxGroupId}-option-${value}`}
              />
              <span id={`${checkboxGroupId}-option-${value}`} aria-hidden>
                {label}
              </span>
            </StyledLabel>
          </StyledListItem>
        ))}
      </StyledList>
    </StyledFieldset>
  );
};

const StyledFieldset = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`;

const StyledList = styled(List)`
  width: 100%;
`;

const StyledListItem = styled(ListItem)`
  width: 100%;
`;

const StyledLabel = styled(Label)`
  display: flex;
  flex-direction: row;
  gap: ${spaceM};
  font-size: ${scale080};
  line-height: ${scale110};
`;

const ErrorMessage = styled(Meta)`
  display: flex;
  gap: ${spaceXS};
  line-height: ${scale080};
  margin-bottom: ${spaceL};
  && {
    color: ${xdlColorError};
  }
`;

const HelperMessage = styled(Meta)`
  display: flex;
  gap: ${spaceXS};
  line-height: ${scale080};
  margin-bottom: ${spaceL};
`;

export const ErrorIcon = styled(IconWarning)`
  flex-shrink: 0;
  height: ${scale080};
  width: ${scale080};
`;

export const InfoIcon = styled(IconInfo)`
  flex-shrink: 0;
  height: ${scale080};
  width: ${scale080};
`;
